/**
 * Card Component - UNIFIED VERSION
 *
 * Consolida las dos implementaciones de Card:
 * - /shared/components/ui/Card/Card.tsx (377 imports)
 * - /components/ui/Card.tsx (377 imports)
 *
 * Features combinadas:
 * - CVA para mejor organización de variantes
 * - Variantes temáticas (default, primary, leads, clients, properties)
 * - Sistema de padding mejorado
 * - Sub-components completos (Header, Title, Description, Content, Footer)
 * - Dark mode support
 * - Accessibility improvements
 */

import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/utils/cn';

// ============================================================================
// VARIANTS DEFINITION
// ============================================================================

const cardVariants = cva(
  // Base classes
  'rounded-xl transition-all duration-200',
  {
    variants: {
      variant: {
        // Solid variants (original Card.tsx style)
        default: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700',
        elevated: 'bg-white dark:bg-gray-800 shadow-md hover:shadow-lg dark:shadow-gray-900/20',
        outline: 'border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500',
        ghost: 'bg-gray-50/50 dark:bg-gray-800/50 hover:bg-gray-100/50 dark:hover:bg-gray-700/50',

        // Glass variants (segunda implementación)
        glass: 'bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl border border-gray-200/50 dark:border-gray-800/50',

        // Themed variants (segunda implementación)
        primary: 'bg-gradient-to-br from-primary-50/60 to-white/60 dark:from-primary-900/30 dark:to-gray-950/60 backdrop-blur-xl border border-primary-200/50 dark:border-primary-800/40',
        leads: 'bg-gradient-to-br from-danger-50/60 to-white/60 dark:from-danger-900/30 dark:to-gray-950/60 backdrop-blur-xl border border-danger-200/50 dark:border-danger-800/40',
        clients: 'bg-gradient-to-br from-success-50/60 to-white/60 dark:from-success-900/30 dark:to-gray-950/60 backdrop-blur-xl border border-success-200/50 dark:border-success-800/40',
        properties: 'bg-gradient-to-br from-info-50/60 to-white/60 dark:from-info-900/30 dark:to-gray-950/60 backdrop-blur-xl border border-info-200/50 dark:border-info-800/40'
      },
      elevation: {
        none: '',
        low: 'shadow-sm hover:shadow-md',
        medium: 'shadow-md hover:shadow-lg',
        high: 'shadow-lg hover:shadow-xl'
      },
      padding: {
        none: 'p-0',
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8',
        xl: 'p-10'
      }
    },
    defaultVariants: {
      variant: 'default',
      elevation: 'none',
      padding: 'md'
    }
  }
);

const cardHeaderVariants = cva(
  'flex flex-col space-y-1.5',
  {
    variants: {
      padding: {
        none: 'p-0',
        sm: 'p-4 pb-3',
        md: 'pb-4',
        lg: 'pb-6',
        xl: 'pb-8'
      }
    },
    defaultVariants: {
      padding: 'md'
    }
  }
);

const cardContentVariants = cva(
  'text-gray-900 dark:text-gray-100',
  {
    variants: {
      padding: {
        none: 'p-0',
        sm: 'p-4 pt-0',
        md: 'pt-0',
        lg: 'pt-0',
        xl: 'pt-0'
      }
    },
    defaultVariants: {
      padding: 'md'
    }
  }
);

const cardFooterVariants = cva(
  'flex items-center border-t border-gray-200 dark:border-gray-700',
  {
    variants: {
      padding: {
        none: 'p-0',
        sm: 'p-4 pt-3',
        md: 'pt-4',
        lg: 'pt-6',
        xl: 'pt-8'
      }
    },
    defaultVariants: {
      padding: 'md'
    }
  }
);

// ============================================================================
// TYPESCRIPT INTERFACES
// ============================================================================

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  children: React.ReactNode;
}

export interface CardHeaderProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardHeaderVariants> {
  children: React.ReactNode;
}

export interface CardTitleProps
  extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export interface CardDescriptionProps
  extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode;
}

export interface CardContentProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardContentVariants> {
  children: React.ReactNode;
}

export interface CardFooterProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardFooterVariants> {
  children: React.ReactNode;
}

// ============================================================================
// COMPONENTS
// ============================================================================

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, elevation, padding, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant, elevation, padding }), className)}
      {...props}
    >
      {children}
    </div>
  )
);

Card.displayName = 'Card';

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, padding, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardHeaderVariants({ padding }), className)}
      {...props}
    >
      {children}
    </div>
  )
);

CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, as: Component = 'h3', children, ...props }, ref) => (
    <Component
      ref={ref}
      className={cn(
        'text-lg font-semibold leading-none tracking-tight text-gray-900 dark:text-white',
        className
      )}
      {...props}
    >
      {children}
    </Component>
  )
);

CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className, children, ...props }, ref) => (
    <p
      ref={ref}
      className={cn('text-sm text-gray-600 dark:text-gray-400', className)}
      {...props}
    >
      {children}
    </p>
  )
);

CardDescription.displayName = 'CardDescription';

export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, padding, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardContentVariants({ padding }), className)}
      {...props}
    >
      {children}
    </div>
  )
);

CardContent.displayName = 'CardContent';

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, padding, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardFooterVariants({ padding }), className)}
      {...props}
    >
      {children}
    </div>
  )
);

CardFooter.displayName = 'CardFooter';

// ============================================================================
// MIGRATION HELPERS
// ============================================================================

/**
 * Deprecated variants mapping
 * Para facilitar la migración desde la versión anterior
 */
export const DEPRECATED_VARIANT_MAPPING = {
  default: 'default',
  elevated: 'elevated',
  outline: 'outline',
  ghost: 'ghost',
  glass: 'glass',
  primary: 'primary',
  leads: 'leads',
  clients: 'clients',
  properties: 'properties'
} as const;

/**
 * Migration guide:
 *
 * FROM: /components/ui/Card.tsx
 * TO: /shared/components/ui/Card/Card.tsx
 *
 * 1. Cambiar import:
 *    - import { Card } from '@/shared/components/ui/Card'
 *    + import { Card } from '@/shared/components/ui/Card'
 *
 * 2. Props mantienen compatibilidad:
 *    - variant, elevation, padding siguen funcionando
 *    - Se agregó 'as' prop a CardTitle para semántica
 *
 * 3. Nuevas variantes disponibles:
 *    - variant="leads" → Para módulo leads
 *    - variant="clients" → Para módulo clients
 *    - variant="properties" → Para módulo properties
 *
 * Ejemplo:
 *
 * <Card variant="leads" elevation="medium" padding="lg">
 *   <CardHeader>
 *     <CardTitle>Leads Overview</CardTitle>
 *     <CardDescription>Manage your leads</CardDescription>
 *   </CardHeader>
 *   <CardContent>
 *     Content here
 *   </CardContent>
 *   <CardFooter>
 *     Footer actions
 *   </CardFooter>
 * </Card>
 */
